import React, { FC, useState } from 'react';
import { Box, Button, makeStyles } from '@dxb-web-ui-framework/design-system';
import clsx from 'clsx';
import { GenericObject, Nullable } from '@dxb-web-ui-framework/common/types';
import { Favorite } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks/DataService';
import { isEmpty } from '@dxb-web-ui-framework/common/utils/Helper';
import { beneficiaryService } from '../../../../../channels/secure-web/services';
import { useBeneficiariesRefetch } from '../../../../beneficiary/src/contexts/Beneficiary/hooks';
import { useWizardStepData } from '../../../../content-composer/src/contexts/ClientSideWizard/hooks';
import { ErrorAlert } from '../../../../content-composer/src/components/Generic/ErrorAlert/ErrorAlert';
import { useSelectedCustomerId } from '../../../../userProfile/src/contexts/UserInfo/hooks';
import { useCurrencies } from '../../contexts/FundTransfer/hooks';
import { RoutingResponseBody } from '../../domain/Transfer/entities/RoutingResponseDTO';
import { SwiftCode } from '../../domain/Transfer/entities';
import { ForeCastFormData } from '../types';

type SaveFavouriteBeneficiaryButtonProps = {
  routingResponseData: RoutingResponseBody;
  foreCastFormData: ForeCastFormData;
  selectedBankSwiftCode: Nullable<SwiftCode>;
  isLoading: boolean;
  isExternalTransferType: boolean;
};

const useStyles = makeStyles(theme => ({
  button: {
    color: 'rgba(0, 0, 0, 0.26) !important',
  },
}));

const SaveFavouriteBeneficiaryButton: FC<SaveFavouriteBeneficiaryButtonProps> = ({
  routingResponseData,
  foreCastFormData,
  selectedBankSwiftCode,
  isLoading,
  isExternalTransferType,
}) => {
  const { t } = useTranslation();
  const customerId = useSelectedCustomerId();
  const currencies = useCurrencies();
  const beneficiariesRefetch = useBeneficiariesRefetch();
  const [isSaveFavouriteButtonClicked, setIsFavouriteButtonIsClicked] = useState<boolean>(false);
  const [timestamp, setTimestamp] = useState(new Date().toISOString());
  const [beneficiaryId, setBeneficiaryId] = useState<string | undefined>(undefined);
  const { values } = useWizardStepData('initTransfer') as { values: GenericObject };
  const currencyShortDescription = currencies.find(
    currency => currency.serialNumber === Number(routingResponseData.debitCurrency),
  )?.shortDescription;
  const classes = useStyles();

  const beneficiaryValuesConstructedByTransferType = isExternalTransferType
    ? {
        beneficiaryCity: values.forecastFormData?.receiverCity,
        beneficiaryAddress: values.forecastFormData?.receiverAddress,
        beneficiaryCountry: values?.beneficiaryCountry?.description,
      }
    : null;

  const {
    data: createFavouriteBeneficiaryData,
    error: createFavouriteBeneficiaryError,
    isLoading: isLoadingCreateFavouriteBeneficiary,
  } = useInvokeSWRService(
    beneficiaryService.CreateBeneficiary,
    {
      customerId,
      data: {
        name: routingResponseData?.creditorFullNameUnMasked || foreCastFormData?.receiverFullName,
        data: {
          iban: routingResponseData?.iban,
          bankName: routingResponseData?.bankName || foreCastFormData?.bankName || '-',
          bic: routingResponseData?.bic || foreCastFormData?.receiverBic,
          creditorFullName: routingResponseData?.creditorFullNameUnMasked || foreCastFormData?.receiverFullName,
          bankCountry: values?.bankCountry?.description || '-',
          bankCity: routingResponseData?.bankCity || selectedBankSwiftCode?.city || '-',
          bankAddress: routingResponseData?.bankAddress || selectedBankSwiftCode?.address || '-',
          debitCurrency: Number(routingResponseData.debitCurrency),
          debitCurrDescription: currencyShortDescription,
          ecommbx: routingResponseData.ecommbx,
          ...beneficiaryValuesConstructedByTransferType,
        },
        meta: {
          timestamp,
        },
      },
      successCallback: () => beneficiariesRefetch && beneficiariesRefetch(),
    },
    isSaveFavouriteButtonClicked,
    'beneficiaryService.CreateBeneficiary',
  );

  const shouldDeleteBeneficiary = Boolean(beneficiaryId) && !isSaveFavouriteButtonClicked;

  const { error: deleteBeneficiaryError, isLoading: isLoadingDeleteBeneficiary } = useInvokeSWRService(
    beneficiaryService.DeleteBeneficiary,
    {
      customerId,
      beneficiaryId,
      successCallback: () => beneficiariesRefetch && beneficiariesRefetch(),
      meta: {
        timestamp,
      },
    },
    shouldDeleteBeneficiary,
    'beneficiariesService.DeleteBeneficiary',
  );

  const isLoadingData = isLoadingCreateFavouriteBeneficiary || isLoadingDeleteBeneficiary || isLoading;

  const handleClickSaveFavouriteBeneficiary = () => {
    setIsFavouriteButtonIsClicked(prev => !prev);
    if (isSaveFavouriteButtonClicked) {
      setBeneficiaryId(createFavouriteBeneficiaryData?.id);
    } else {
      setBeneficiaryId(undefined);
    }
    setTimestamp(new Date().toISOString());
  };

  return (
    <>
      {!isEmpty(createFavouriteBeneficiaryError) && (
        <Box mt={4}>
          <ErrorAlert message={createFavouriteBeneficiaryError?.message} />
        </Box>
      )}
      {!isEmpty(deleteBeneficiaryError) && (
        <Box mt={4}>
          <ErrorAlert message={deleteBeneficiaryError?.message} />
        </Box>
      )}
      <Box mt={!isEmpty(createFavouriteBeneficiaryError) || !isEmpty(deleteBeneficiaryError) ? 0 : 3}>
        <Button
          color='primary'
          size='large'
          startIcon={<Favorite />}
          classes={{ root: clsx({ [classes.button]: !isSaveFavouriteButtonClicked }) }}
          variant='text'
          title={t('fundTransferPage.transferConfirm.saveFavouriteBeneficiaryButton.label')}
          disabled={isLoadingData}
          onClick={handleClickSaveFavouriteBeneficiary}>
          {t('fundTransferPage.transferConfirm.saveFavouriteBeneficiaryButton.label')}
        </Button>
      </Box>
    </>
  );
};

export { SaveFavouriteBeneficiaryButton };
